/**
 * Sales Board メール統合 API hooks (Phase 3 第1層)
 *
 * GET /api/sales-pipelines/email-status-summary
 * GET /api/sales-pipelines/:id/email-timeline
 * GET /api/sales-pipelines/:id/stage-suggestion
 * POST /api/sales-pipelines/:id/apply-stage-suggestion
 */

import { RestApiError, restClient } from "lib/rest-client";
import { useCallback, useEffect, useState } from "react";

export type PipelineEmailStatus =
  | "not_sent"
  | "waiting_reply"
  | "has_reply"
  | "stale";

export type PipelineEmailStatusSummary = {
  salesPipelineId: string;
  status: PipelineEmailStatus;
  lastEmailAt: string | null;
  elapsedHours: number | null;
  lastSubject: string | null;
  unrespondedReplies: number;
};

type PipelineEmailStatusSummaryListResponse = {
  items: PipelineEmailStatusSummary[];
};

export type PipelineEmailTimelineItem = {
  direction: "sent" | "received";
  occurredAt: string;
  subject: string;
  fromTo: string;
  bodyPreview: string;
  sentEmailId: string | null;
  replyEmailId: string | null;
};

type PipelineEmailTimelineResponse = {
  items: PipelineEmailTimelineItem[];
};

export type StageSuggestion = {
  salesPipelineId: string;
  currentStage: string;
  suggestedStage: string | null;
  reason: string;
  triggeredByReplyId: string | null;
};

/**
 * 全パイプラインのメール状況を一括取得する hook
 * @return {object} summaries (id → summary map), loading, error, refetch
 */
export function usePipelineEmailStatusSummary(): {
  summaries: Record<string, PipelineEmailStatusSummary>;
  loading: boolean;
  error: RestApiError | null;
  refetch: () => Promise<void>;
} {
  const [summaries, setSummaries] = useState<
    Record<string, PipelineEmailStatusSummary>
  >({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<RestApiError | null>(null);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response =
        await restClient.get<PipelineEmailStatusSummaryListResponse>(
          "/sales-pipelines/email-status-summary",
        );
      const next: Record<string, PipelineEmailStatusSummary> = {};
      for (const item of response.items) {
        next[item.salesPipelineId] = item;
      }
      setSummaries(next);
    } catch (e) {
      if (e instanceof RestApiError) {
        setError(e);
      } else {
        setError(
          new RestApiError(
            e instanceof Error ? e.message : "Unknown error",
            500,
          ),
        );
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  return { summaries, loading, error, refetch: fetchSummary };
}

/**
 * 特定パイプラインのメールタイムラインを取得する hook
 * @param {object} options オプション
 * @param {string|null} options.pipelineId パイプライン ID（null なら fetch しない）
 * @return {object} items, loading, error, refetch
 */
export function usePipelineEmailTimeline(options: {
  pipelineId: string | null;
}): {
  items: PipelineEmailTimelineItem[];
  loading: boolean;
  error: RestApiError | null;
  refetch: () => Promise<void>;
} {
  const { pipelineId } = options;
  const [items, setItems] = useState<PipelineEmailTimelineItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<RestApiError | null>(null);

  const fetchTimeline = useCallback(async () => {
    if (!pipelineId) {
      setItems([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await restClient.get<PipelineEmailTimelineResponse>(
        `/sales-pipelines/${pipelineId}/email-timeline`,
      );
      setItems(response.items);
    } catch (e) {
      if (e instanceof RestApiError) {
        setError(e);
      } else {
        setError(
          new RestApiError(
            e instanceof Error ? e.message : "Unknown error",
            500,
          ),
        );
      }
    } finally {
      setLoading(false);
    }
  }, [pipelineId]);

  useEffect(() => {
    fetchTimeline();
  }, [fetchTimeline]);

  return { items, loading, error, refetch: fetchTimeline };
}

/**
 * ステージ遷移候補を取得する hook
 * @param {object} options オプション
 * @param {string|null} options.pipelineId パイプライン ID（null なら fetch しない）
 * @return {object} suggestion, loading, error, refetch
 */
export function useStageSuggestion(options: { pipelineId: string | null }): {
  suggestion: StageSuggestion | null;
  loading: boolean;
  error: RestApiError | null;
  refetch: () => Promise<void>;
} {
  const { pipelineId } = options;
  const [suggestion, setSuggestion] = useState<StageSuggestion | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<RestApiError | null>(null);

  const fetchSuggestion = useCallback(async () => {
    if (!pipelineId) {
      setSuggestion(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await restClient.get<StageSuggestion>(
        `/sales-pipelines/${pipelineId}/stage-suggestion`,
      );
      setSuggestion(response);
    } catch (e) {
      if (e instanceof RestApiError) {
        setError(e);
      } else {
        setError(
          new RestApiError(
            e instanceof Error ? e.message : "Unknown error",
            500,
          ),
        );
      }
    } finally {
      setLoading(false);
    }
  }, [pipelineId]);

  useEffect(() => {
    fetchSuggestion();
  }, [fetchSuggestion]);

  return { suggestion, loading, error, refetch: fetchSuggestion };
}

/**
 * ステージ遷移候補を適用する hook
 * @return {object} applyStageSuggestion, loading, error
 */
export function useApplyStageSuggestion(): {
  applyStageSuggestion: (
    pipelineId: string,
    suggestedStage: string,
  ) => Promise<void>;
  loading: boolean;
  error: RestApiError | null;
} {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<RestApiError | null>(null);

  const applyStageSuggestion = useCallback(
    async (pipelineId: string, suggestedStage: string) => {
      setLoading(true);
      setError(null);
      try {
        await restClient.post(
          `/sales-pipelines/${pipelineId}/apply-stage-suggestion`,
          { suggestedStage },
        );
      } catch (e) {
        if (e instanceof RestApiError) {
          setError(e);
          throw e;
        }
        const wrapped = new RestApiError(
          e instanceof Error ? e.message : "Unknown error",
          500,
        );
        setError(wrapped);
        throw wrapped;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { applyStageSuggestion, loading, error };
}
